import { faLinkedin, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { faArrowUpRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ExternalLink } from "~/components/site/external-link";
import { Author, AUTHORS } from "~/lib/authors";
import { formatUrl } from "~/lib/format-url";

type SpeakerId =
  | "ankur-wadehra"
  | "ben-milne"
  | "cuy-sheffield"
  | "emily-nicolle"
  | "gaetan-thabot"
  | "jenny-johnston"
  | "matt-harris"
  | "nik-milanovic"
  | "rita-martins"
  | "ryan-rugg"
  | "zachary-anderson-pettet";

type ConferenceId =
  | "rwa"
  | "permissionless"
  | "meridian"
  | "money2020"
  | "disrupt";

type Speaker = Readonly<{
  id: SpeakerId;
  name: string;
  avatar: string;
  title: string;
  company: string;
  url?: string;
}>;

type Session = Readonly<{
  name: string;
  desc: string;
  url?: string;
  date: string;
  time: string;
  calUrl?: string;
  speakers: SpeakerId[];
}>;

type Conference = Readonly<{
  id: ConferenceId;
  name: string;
  date: string;
  location: string;
  graphic: string;
  href: string;
  attendees: (keyof typeof AUTHORS)[];
  session?: Session[];
}>;

const ben = ["ben-milne"];

const speakers: Speaker[] = [
  {
    id: "ankur-wadehra",
    name: "Ankur Wadehra",
    avatar: "/assets/photos/ankur-wadehra.png",
    title: "Head of Product",
    company: "Zodia Custody",
    url: "https://zodia-custody.com/",
  },
  {
    id: "ben-milne",
    name: "Ben Milne",
    avatar: "/assets/blog/authors/ben-milne.png",
    title: "Founder & CEO",
    company: "Brale",
    url: "http://brale.com/",
  },
  {
    id: "cuy-sheffield",
    name: "Cuy Sheffield",
    avatar: "/assets/photos/cuy-sheffield.png",
    title: "Head of Crypto",
    company: "Visa",
    url: "http://visa.com/",
  },
  {
    id: "emily-nicolle",
    name: "Emiy Nicolle",
    avatar: "/assets/photos/emily-nicolle.png",
    title: "Crypto Reporter",
    company: "Bloomberg News",
    url: "https://www.bloomberg.com/authors/AVmRMyBPWU8/emily-nicolle",
  },
  {
    id: "gaetan-thabot",
    name: "Gaetan Thabot",
    avatar: "/assets/photos/gaetan-thabot.png",
    title: "Head of Protocols",
    company: "Blockdaemon",
    url: "https://www.blockdaemon.com/",
  },
  {
    id: "jenny-johnston",
    name: "Jenny Johnston",
    avatar: "/assets/photos/jenny-johnston.png",
    title: "Account Director",
    company: "OpenAI",
    url: "https://openai.com/",
  },
  {
    id: "matt-harris",
    name: "Matt Harris",
    avatar: "/assets/photos/matt-harris.png",
    title: "Partner",
    company: "Bain Capital Ventures",
    url: "https://www.baincapital.com/",
  },
  {
    id: "nik-milanovic",
    name: "Nik Milanović",
    avatar: "/assets/photos/nik-milanovic.png",
    title: "Founder",
    company: "This Week in Fintech",
    url: "https://www.thisweekinfintech.com/",
  },
  {
    id: "rita-martins",
    name: "Rita Martins",
    avatar: "/assets/photos/rita-martins.png",
    title: "Author",
    company: "Web3 in Financial Services",
    url: "https://www.ritamartins.co.uk/",
  },
  {
    id: "ryan-rugg",
    name: "Ryan Rugg",
    avatar: "/assets/photos/ryan-rugg.png",
    title: "Global Head of Digital Assets",
    company: "Citi",
    url: "https://www.citi.com/",
  },
  {
    id: "zachary-anderson-pettet",
    name: "Zachary Anderson Pettet",
    avatar: "/assets/photos/zachary-anderson-pettet.png",
    title: "VP, Fintech Strategy",
    company: "Money20/20",
    url: "https://us.money2020.com/",
  },
];

const conferences: Conference[] = [
  {
    id: "rwa",
    name: "RWA Day",
    date: "Oct 8",
    location: "Salt Lake City, UT",
    graphic: "graphic-rwa.png",
    href: "https://rwa.day/",
    attendees: ["chase-merlin"],
  },
  {
    id: "permissionless",
    name: "Permissionless III",
    date: "Oct 9-11",
    location: "Salt Lake City, UT",
    graphic: "graphic-permissionless.png",
    href: "https://blockworks.co/event/permissionless-iii",
    attendees: ["chase-merlin", "michael-mcallister"],
  },
  {
    id: "meridian",
    name: "Meridian",
    date: "Oct 15-17",
    location: "London, UK",
    graphic: "graphic-meridian.png",
    href: "https://meridian.stellar.org/",
    attendees: ["ben-milne"],
    session: [
      {
        name: "How Institutions can Navigate the DeFi Landscape",
        desc: "Unlock the future of finance as experts from Blockdaemon, Brale, London Stock Exchange and Zodia Custody delve into how institutions are embracing DeFi to stay ahead in a rapidly evolving landscape. From overcoming regulatory hurdles to pioneering innovative solutions, this session will explore the challenges, opportunities and strategies that are shaping the future of finance.",
        url: "https://meridian.stellar.org/agenda",
        date: "Wed, Oct 16",
        time: "4:10 - 4:45 PM BST",
        calUrl:
          "https://calendar.google.com/calendar/u/0/r/eventedit?text=How+Institutions+can+Navigate+the+DeFi+Landscape&dates=20241016T161000/20241016T164500&details=Unlock+the+future+of+finance+as+experts+from+Blockdaemon%2C+Brale%2C+London+Stock+Exchange+and+Zodia+Custody+delve+into+how+institutions+are+embracing+DeFi+to+stay+ahead+in+a+rapidly+evolving+landscape.+From+overcoming+regulatory+hurdles+to+pioneering+innovative+solutions%2C+this+session+will+explore+the+challenges%2C+opportunities+and+strategies+that+are+shaping+the+future+of+finance.%0A%0AEvent+URL%3A+https%3A%2F%2Fmeridian.stellar.org%2Fagenda&location=Level+1%2C+Stellar+Court+(Main+Stage)&ctz=Europe/London",
        speakers: [
          "ankur-wadehra",
          "ben-milne",
          "emily-nicolle",
          "gaetan-thabot",
          "rita-martins",
        ],
      },
    ],
  },
  {
    id: "money2020",
    name: "Money20/20",
    date: "Oct 27-30",
    location: "Las Vegas, NV",
    graphic: "graphic-money2020.png",
    href: "https://us.money2020.com/",
    attendees: ben,
    session: [
      {
        name: "Novel Banking Technologies",
        desc: "As a Banking Leader, understanding what new technologies will create real value for your bank today and ten years from now is always top of mind. How and when to implement those technologies is a constant battle. This panel made up of Builders and Leaders in the weeds with these Technologies every single day will act as a rapid fire primer on the Innovations worth caring about and why. Vice versa, you will also get a sense of what is overhyped and not worth allocating resources to.",
        url: "https://us.money2020.com/agenda/schedule#/agenda/event/novel-banking-technologies-e103-62271",
        date: "Sun, Oct 27",
        time: "9:45 - 10:15 AM PT",
        calUrl:
          "https://calendar.google.com/calendar/u/0/r/eventedit?text=Novel+Banking+Technologies&dates=20241027T164500Z/20241027T171500Z&details=As+a+Banking+Leader%2C+understanding+what+new+technologies+will+create+real+value+for+your+bank+today+and+ten+years+from+now+is+always+top+of+mind.+How+and+when+to+implement+those+technologies+is+a+constant+battle.+This+panel+made+up+of+Builders+and+Leaders+in+the+weeds+with+these+Technologies+every+single+day+will+act+as+a+rapid+fire+primer+on+the+Innovations+worth+caring+about+and+why.+Vice+versa%2C+you+will+also+get+a+sense+of+what+is+overhyped+and+not+worth+allocating+resources+to.%0A%0AEvent+URL%3A+https%3A%2F%2Fus.money2020.com%2Fagenda%2Fschedule%23%2Fagenda%2Fevent%2Fnovel-banking-technologies-e103-62271&location=Marco+Polo+-+701-703%2C+Level+1&ctz=America/Los_Angeles",
        speakers: ["ben-milne", "jenny-johnston", "zachary-anderson-pettet"],
      },
      {
        name: "On the Precipice of Decentralization",
        desc: "Fintech has been slowly deconstructing the analog past of legacy tech stacks, utilizing first principles to undo certain systems. We are at the dawn of decentralizing tools having the capacity and speed to truly decentralize financial services. Through tokenization, and the immediacy of stablecoins, bankers will have to rethink the entire nature of they intend to move money. This panel will paint the vision for a future that is starting right now.",
        url: "https://us.money2020.com/agenda/schedule#/agenda/event/on-the-precipice-of-decentralization-e103-62221",
        date: "Sun, Oct 27",
        time: "12:35 - 1:00 PM PT",
        calUrl:
          "https://calendar.google.com/calendar/u/0/r/eventedit?text=On+the+Precipice+of+Decentralization&dates=20241027T193500Z/20241027T200000Z&details=Fintech+has+been+slowly+deconstructing+the+analog+past+of+legacy+tech+stacks%2C+utilizing+first+principles+to+undo+certain+systems.+We+are+at+the+dawn+of+decentralizing+tools+having+the+capacity+and+speed+to+truly+decentralize+financial+services.+Through+tokenization%2C+and+the+immediacy+of+stablecoins%2C+bankers+will+have+to+rethink+the+entire+nature+of+they+intend+to+move+money.+This+panel+will+paint+the+vision+for+a+future+that+is+starting+right+now.%0A%0AEvent+URL%3A+https%3A%2F%2Fus.money2020.com%2Fagenda%2Fschedule%23%2Fagenda%2Fevent%2Fon-the-precipice-of-decentralization-e103-62221&location=Marco+Polo+-+701-703%2C+Level+1&ctz=America/Los_Angeles",
        speakers: ["ben-milne", "matt-harris", "ryan-rugg"],
      },
    ],
  },
  {
    id: "disrupt",
    name: "TechCrunch Disrupt",
    date: "Oct 28-30",
    location: "San Francisco, CA",
    graphic: "graphic-disrupt.png",
    href: "https://techcrunch.com/events/tc-disrupt-2024/",
    attendees: ben,
    session: [
      {
        name: "Innovation in Stablecoins",
        desc: "Explore how stablecoins are transforming payments and global finance. This panel will cover the latest innovations, real-world applications, and challenges in integrating stablecoins into global financial systems. Gain insights into the future of digital assets and their role in reshaping the fintech landscape.",
        url: "https://techcrunch.com/events/tc-disrupt-2024/agenda/",
        date: "Wed, Oct 30",
        time: "2:30 PM PT",
        calUrl:
          "https://calendar.google.com/calendar/u/0/r/eventedit?text=Innovation+in+Stablecoins&dates=20241030T213000Z/20241030T223000Z&details=Explore+how+stablecoins+are+transforming+payments+and+global+finance.+This+panel+will+cover+the+latest+innovations,+real-world+applications,+and+challenges+in+integrating+stablecoins+into+global+financial+systems.+Gain+insights+into+the+future+of+digital+assets+and+their+role+in+reshaping+the+fintech+landscape.%0A%0AEvent+URL:+https://techcrunch.com/events/tc-disrupt-2024/agenda/&location=TechCrunch+Disrupt+2024&ctz=America/Los_Angeles",
        speakers: ["ben-milne", "cuy-sheffield", "nik-milanovic"],
      },
    ],
  },
];

type Props = Readonly<{
  id: ConferenceId;
  className?: string;
}>;

export function Conference({ id, className = "" }: Props): JSX.Element {
  const conference = conferences.find((conf) => conf.id === id);

  if (!conference) {
    return <div>Conference not found</div>;
  }

  const attendees = conference.attendees.map((key) => AUTHORS[key]);

  return (
    <div className={`mt-16 border-t border-primary pt-12 ${className}`}>
      <div className="flex flex-col gap-y-2 md:flex-row md:justify-between md:items-center lg:flex-col lg:gap-y-2 lg:items-start xl:flex-row xl:justify-between xl:items-center">
        <h2 className="m-0">{conference.name}</h2>
        <div className="flex items-center gap-4 text-secondary text-xl font-semibold">
          <span className="m-0">{conference.date}, 2024</span>
          <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
          <span className="m-0">{conference.location}</span>
        </div>
      </div>
      <div className="round border border-primary not-prose mt-6 mb-4 bg-black overflow-clip">
        <ExternalLink
          href={conference.href}
          title={`Read more on ${formatUrl(conference.href)}`}
          className="underline text-primary hover:no-underline flex items-center gap-x-1 print:no-underline"
        >
          <img
            alt={`${conference.name} graphic`}
            src={`/assets/blog/october-conferences-for-brale/${conference.graphic}`}
          />
        </ExternalLink>
      </div>
      <div className="not-prose text-gray-500 dark:text-gray-400 text-sm flex justify-between mb-10">
        <div className="flex items-center gap-x-1">
          <span>Source: </span>
          <ExternalLink
            href={conference.href}
            title={`Read more on ${formatUrl(conference.href)}`}
            className="underline text-primary hover:no-underline flex items-center gap-x-1 print:no-underline"
          >
            <span>{formatUrl(conference.href)}</span>
            <FontAwesomeIcon
              className="h-4 w-4 text-primary print:hidden"
              icon={faArrowUpRight}
            />
          </ExternalLink>
        </div>
      </div>
      <Attendees attendees={attendees} />
      {conference.session && (
        <Session session={conference.session} speakers={speakers} />
      )}
    </div>
  );
}

function Attendees({ attendees }: { attendees: Author[] }): JSX.Element {
  const attendeeText = attendees.length === 1 ? "Attendee" : "Attendees";

  return (
    <>
      <div className="text-xl font-semibold text-black dark:text-white mb-4">
        {attendeeText}
      </div>
      <ul className="grid sm:grid-cols-2 gap-4 md:gap-6 mb-10 m-0 p-0">
        {attendees.map((a) => (
          <li className="flex items-center gap-x-6 m-0" key={a.name}>
            <img
              alt={`${a.name} | Brale`}
              className="w-24 h-24 rounded-full"
              src={`${a.avatar}`}
            />
            <div className="flex flex-col">
              <div className="eyebrow text-primary !mb-0">{a.name}</div>
              <span className="text-base mb-2">{a.jobTitle}</span>
              <div className="flex gap-x-3">
                {a.social.twitter && (
                  <ExternalLink
                    aria-label={`${a.name} on Twitter`}
                    href={a.social.twitter}
                    className="leading-none"
                  >
                    <FontAwesomeIcon
                      className="h-5 w-5 text-gray-500"
                      icon={faXTwitter}
                    />
                  </ExternalLink>
                )}
                {a.social.linkedin && (
                  <ExternalLink
                    aria-label={`${a.name} on LinkedIn`}
                    href={a.social.linkedin}
                    className="leading-none"
                  >
                    <FontAwesomeIcon
                      className="h-5 w-5 text-gray-500"
                      icon={faLinkedin}
                    />
                  </ExternalLink>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function Session({
  session,
  speakers,
}: {
  session: Session[];
  speakers: Speaker[];
}): JSX.Element {
  const sessionText = session.length === 1 ? "Session" : "Sessions";

  return (
    <>
      <div className="text-xl font-semibold text-black dark:text-white mb-4">
        {sessionText}
      </div>
      {session.map((s, i) => (
        <div key={i} className="flex flex-col gap-y-2 mb-14">
          <ExternalLink
            href={s.url}
            title={`Read more about ${s.name}`}
            className="underline text-primary hover:no-underline flex items-center gap-x-1 print:no-underline"
          >
            <h3 className="m-0 h4">{s.name}</h3>
          </ExternalLink>
          <div className="flex flex-col md:flex-row gap-4 text-secondary text-lg font-semibold">
            <div className="flex items-center gap-4">
              <span className="m-0">{s.date}</span>
              <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
              <span className="m-0">{s.time}</span>
            </div>
            {s.calUrl && (
              <div className="flex items-center gap-4">
                <div className="h-2 w-2 bg-gray-400 rounded-full hidden md:inline-block"></div>
                <ExternalLink
                  href={s.calUrl}
                  title="Add to calendar"
                  className="underline text-primary hover:no-underline flex items-center gap-x-1 print:no-underline"
                >
                  <span>Add to calendar</span>
                  <FontAwesomeIcon
                    className="h-4 w-4 text-primary print:hidden"
                    icon={faArrowUpRight}
                  />
                </ExternalLink>
              </div>
            )}
          </div>
          <p className="mb-0">{s.desc}</p>
          <Speakers
            speakers={speakers.filter((speaker) =>
              s.speakers.includes(speaker.id)
            )}
          />
        </div>
      ))}
    </>
  );
}

function Speakers({ speakers }: { speakers: Speaker[] }): JSX.Element {
  return (
    <>
      <div className="text-base font-semibold text-black dark:text-white my-4">
        Speakers
      </div>
      <ul className="grid sm:grid-cols-2 gap-4 md:gap-6 m-0 p-0">
        {speakers.map((s) => (
          <li key={s.id} className="flex items-center gap-x-6 m-0">
            <img
              alt={`${s.name} portrait`}
              className="w-16 h-16 rounded-full"
              src={`${s.avatar}`}
            />
            <div className="flex flex-col">
              <div className="flex items-center">
                <div className="eyebrow text-primary !mb-0">{s.name}</div>
              </div>
              <span className="text-base prose dark:prose-invert font-normal">
                {s.title},&nbsp;
                <ExternalLink
                  href={s.url}
                  title={`Learn more about ${s.name}`}
                  className="relative group global-hover-transition items-center justify-between inline-flex"
                >
                  {s.company}
                  <span className="opacity-0 group-hover:opacity-100 group-hover:translate-x-[4px] transition-all duration-500 leading-none">
                    <FontAwesomeIcon
                      icon={faArrowUpRight}
                      className="h-4 w-4"
                    />
                  </span>
                </ExternalLink>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
